/**
 * Trial Utility Functions
 * Safe utility functions for trial period calculations and status management
 */

/**
 * Calculate trial status based on start and end dates
 * @param {string|Date} trialStartDate - Trial start date
 * @param {string|Date} trialEndDate - Trial end date
 * @returns {string} - 'not_started' | 'active' | 'expired'
 */
export const calculateTrialStatus = (trialStartDate, trialEndDate) => {
  if (!trialStartDate || !trialEndDate) {
    return 'not_started';
  }

  const now = new Date();
  const start = new Date(trialStartDate);
  const end = new Date(trialEndDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'not_started';
  }



  if (now < start) return 'not_started';
  if (now > end) return 'expired';
  return 'active';
};

/**
 * Calculate days remaining in trial
 * @param {string|Date} trialEndDate - Trial end date
 * @returns {number} - Days remaining (0 if expired)
 */
export const calculateDaysRemaining = (trialEndDate) => {
  if (!trialEndDate) return 0;

  const now = new Date();
  const end = new Date(trialEndDate);

  if (isNaN(end.getTime())) {
    return 0;
  }

  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

/**
 * Calculate trial progress percentage
 * @param {string|Date} trialStartDate - Trial start date
 * @param {string|Date} trialEndDate - Trial end date
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateTrialProgress = (trialStartDate, trialEndDate) => {
  if (!trialStartDate || !trialEndDate) return 0;

  const now = new Date();
  const start = new Date(trialStartDate);
  const end = new Date(trialEndDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  const totalDays = (end - start) / (1000 * 60 * 60 * 24);
  const elapsedDays = (now - start) / (1000 * 60 * 60 * 24);

  if (totalDays <= 0) return 100;

  const progress = (elapsedDays / totalDays) * 100;
  return Math.min(100, Math.max(0, progress));
};

/**
 * Check if user can access the application
 * @param {boolean} isApproved - User/organization approval status
 * @param {string} subscriptionStatus - Subscription status
 * @param {string} trialStatus - Trial status
 * @param {boolean} bypassLimits - Whether organization has bypass limits enabled
 * @param {string|Date|Timestamp|null} subscriptionEndsAt - Subscription end date (optional)
 * @returns {boolean} - Whether user can access the app
 */
export const canAccessApp = (isApproved, subscriptionStatus, trialStatus, bypassLimits = false, subscriptionEndsAt = null) => {
  // Must be approved first
  if (!isApproved) return false;

  // Bypass limits - unlimited access
  if (bypassLimits) return true;

  // Active subscription - but check if it has expired
  // FIX: Issue #3 - Check subscriptionEndsAt to prevent access after expiration
  if (subscriptionStatus === 'active') {
    if (subscriptionEndsAt) {
      // Normalize subscriptionEndsAt to Date for comparison
      let expiryDate;
      if (typeof subscriptionEndsAt === 'string') {
        expiryDate = new Date(subscriptionEndsAt);
      } else if (subscriptionEndsAt && typeof subscriptionEndsAt.toDate === 'function') {
        // Firestore Timestamp
        expiryDate = subscriptionEndsAt.toDate();
      } else if (subscriptionEndsAt instanceof Date) {
        expiryDate = subscriptionEndsAt;
      } else {
        // Invalid date format, allow access (defensive)
        return true;
      }
      
      // Check if subscription has expired
      if (expiryDate <= new Date()) {
        return false; // Subscription has expired
      }
    }
    return true;
  }

  // Trial access
  if (subscriptionStatus === 'trial' && trialStatus === 'active') return true;

  // No access for expired trials or cancelled subscriptions
  return false;
};

/**
 * Check if user can access dashboard based on subscription plan
 * @param {string} subscriptionPlan - Current subscription plan
 * @param {boolean} bypassLimits - Whether organization has bypass limits enabled
 * @returns {boolean} - Whether user can access dashboard
 */
export const canAccessDashboard = (subscriptionPlan, bypassLimits = false) => {
  // Bypass limits - unlimited access
  if (bypassLimits) return true;

  // Trial users can access dashboard (no subscription plan or explicitly trial)
  if (!subscriptionPlan || subscriptionPlan === 'trial') return true;

  // Only Premium, Premium+, and Enterprise plans can access dashboard
  // Basic plan users cannot access dashboard
  const allowedPlans = ['premium', 'premiumPlus', 'enterprise'];
  return allowedPlans.includes(subscriptionPlan);
};

/**
 * Check if subscription is expired (handles both trial and subscription expiration)
 * @param {string} subscriptionStatus - Subscription status
 * @param {string|Date|Timestamp|null} subscriptionEndsAt - Subscription end date (optional)
 * @returns {boolean} - Whether subscription is expired
 */
export const isSubscriptionExpired = (subscriptionStatus, subscriptionEndsAt = null) => {
  // Check if subscription status is explicitly expired
  if (subscriptionStatus === 'expired') {
    return true;
  }
  
  // Check if active subscription has passed its end date
  if (subscriptionStatus === 'active' && subscriptionEndsAt) {
    // Normalize subscriptionEndsAt to Date for comparison
    let expiryDate;
    if (typeof subscriptionEndsAt === 'string') {
      expiryDate = new Date(subscriptionEndsAt);
    } else if (subscriptionEndsAt && typeof subscriptionEndsAt.toDate === 'function') {
      // Firestore Timestamp
      expiryDate = subscriptionEndsAt.toDate();
    } else if (subscriptionEndsAt instanceof Date) {
      expiryDate = subscriptionEndsAt;
    } else {
      // Invalid date format, assume not expired (defensive)
      return false;
    }
    
    // Check if subscription has expired
    if (expiryDate <= new Date()) {
      return true; // Subscription has expired
    }
  }
  
  return false;
};

/**
 * Check if trial is expired (handles both trial expiration and subscription expiration)
 * @param {string} subscriptionStatus - Subscription status
 * @param {string} trialStatus - Trial status
 * @param {string|Date|Timestamp|null} subscriptionEndsAt - Subscription end date (optional)
 * @returns {boolean} - Whether trial/subscription is expired
 */
export const isTrialExpired = (subscriptionStatus, trialStatus, subscriptionEndsAt = null) => {
  // Trial expiration
  if (subscriptionStatus === 'trial' && trialStatus === 'expired') {
    return true;
  }
  
  // Subscription expiration (after cancellation or scheduled expiration)
  if (isSubscriptionExpired(subscriptionStatus, subscriptionEndsAt)) {
    return true;
  }
  
  return false;
};

/**
 * Check if organization is on trial
 * @param {string} subscriptionStatus - Subscription status
 * @returns {boolean} - Whether organization is on trial
 */
export const isOnTrial = (subscriptionStatus) => {
  return subscriptionStatus === 'trial';
};

/**
 * Check if organization has active subscription
 * @param {string} subscriptionStatus - Subscription status
 * @returns {boolean} - Whether organization has active subscription
 */
export const hasActiveSubscription = (subscriptionStatus) => {
  return subscriptionStatus === 'active';
};

/**
 * Get trial status display text
 * @param {string} trialStatus - Trial status
 * @param {number} daysRemaining - Days remaining
 * @param {Function} t - Translation function (optional)
 * @returns {string} - Display text for trial status
 */
export const getTrialStatusText = (trialStatus, daysRemaining, t = null) => {
  // If translation function is provided, use translations
  if (t) {
    switch (trialStatus) {
      case 'not_started':
        return t('trial.notStarted');
      case 'active':
        return t('trial.active', { days: daysRemaining });
      case 'expired':
        return t('trial.expired');
      default:
        return t('trial.unknownStatus');
    }
  }
  
  // Fallback to English strings if no translation function
  switch (trialStatus) {
    case 'not_started':
      return 'Trial not started';
    case 'active':
      return `${daysRemaining} days remaining`;
    case 'expired':
      return 'Trial expired';
    default:
      return 'Unknown status';
  }
};

/**
 * Check if notification should be sent
 * @param {string} trialEndDate - Trial end date
 * @param {number} daysBeforeExpiry - Days before expiry to send notification
 * @param {boolean} alreadySent - Whether notification was already sent
 * @returns {boolean} - Whether notification should be sent
 */
export const shouldSendNotification = (trialEndDate, daysBeforeExpiry, alreadySent) => {
  if (alreadySent || !trialEndDate) return false;

  const end = new Date(trialEndDate);
  
  if (isNaN(end.getTime())) return false;

  const daysRemaining = calculateDaysRemaining(trialEndDate);
  return daysRemaining === daysBeforeExpiry;
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatTrialDate = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Get trial status color for UI
 * @param {string} trialStatus - Trial status
 * @param {number} daysRemaining - Days remaining
 * @returns {string} - CSS color class or hex color
 */
export const getTrialStatusColor = (trialStatus, daysRemaining) => {
  switch (trialStatus) {
    case 'not_started':
      return '#6B7280'; // Gray
    case 'active':
      if (daysRemaining <= 3) return '#EF4444'; // Red
      if (daysRemaining <= 7) return '#F59E0B'; // Yellow
      return '#10B981'; // Green
    case 'expired':
      return '#EF4444'; // Red
    default:
      return '#6B7280'; // Gray
  }
};

/**
 * Validate trial data structure
 * @param {Object} trialData - Trial data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateTrialData = (trialData) => {
  const errors = [];

  if (!trialData) {
    errors.push('Trial data is required');
    return { isValid: false, errors };
  }

  // Check required fields
  const requiredFields = ['subscriptionStatus', 'trialStartDate', 'trialEndDate'];
  requiredFields.forEach(field => {
    if (!trialData[field]) {
      errors.push(`${field} is required`);
    }
  });

  // Validate subscription status
  const validStatuses = ['trial', 'active', 'expired', 'cancelled'];
  if (trialData.subscriptionStatus && !validStatuses.includes(trialData.subscriptionStatus)) {
    errors.push(`Invalid subscription status: ${trialData.subscriptionStatus}`);
  }

  // Validate dates
  if (trialData.trialStartDate && isNaN(new Date(trialData.trialStartDate).getTime())) {
    errors.push('Invalid trial start date');
  }

  if (trialData.trialEndDate && isNaN(new Date(trialData.trialEndDate).getTime())) {
    errors.push('Invalid trial end date');
  }

  // Validate date order
  if (trialData.trialStartDate && trialData.trialEndDate) {
    const start = new Date(trialData.trialStartDate);
    const end = new Date(trialData.trialEndDate);
    if (start >= end) {
      errors.push('Trial end date must be after start date');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
